import { getStudioAccessToken } from "@/services/auth-provider";
import {
  cogoPlatformApiUrl,
  withCogoDevProxyHeaders,
} from "@/services/cogo-platform-config";
import type { ApplicationOntologyPublicationRefs } from "@cogo/acceptance-contracts";
import {
  parsePublicationApplicationOntologyRefs,
  parsePublicationReleaseProjection,
  parsePublicationTargetProfileProjection,
  type PublicationCandidateRequest,
  type PublicationContentRef,
  type PublicationCredentialResult,
  type PublicationDeployTransportRequest,
  type PublicationReleaseProjection,
  type PublicationRollbackTransportRequest,
  type PublicationTakeDownTransportRequest,
  type PublicationTargetProfileProjection,
} from "./publication-release-types";

const PUBLICATION_REQUEST_TIMEOUT_MS = 30_000;
const PUBLICATION_PROOF_REQUEST_TIMEOUT_MS = 150_000;

export class PublicationReleaseApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details: unknown;

  constructor(status: number, body: unknown) {
    const record = asRecord(body);
    const message =
      firstString(record.error, record.message) ??
      `Publication request failed: ${status}`;
    super(message);
    this.name = "PublicationReleaseApiError";
    this.status = status;
    this.code = firstString(record.code);
    this.details = body;
  }
}

/**
 * Network, timeout, throttling, and server failures do not prove whether the
 * deployment side effect happened. Preserve the exact idempotency binding so
 * callers can reconcile or safely retry the same request.
 */
export function isAmbiguousPublicationDeployFailure(cause: unknown): boolean {
  if (!(cause instanceof PublicationReleaseApiError)) return true;
  return (
    cause.status >= 500 ||
    cause.status === 408 ||
    cause.status === 425 ||
    cause.status === 429
  );
}

export async function listPublicationReleases(
  projectId: string,
  signal?: AbortSignal,
): Promise<PublicationReleaseProjection[]> {
  let body: unknown;
  try {
    body = await publicationRequest(
      `/api/projects/${encodeURIComponent(projectId)}/publications`,
      { signal },
    );
  } catch (error) {
    // Nothing published yet is not a publish FAILURE. Measured live on the
    // appliance: this path answers 404 there -- it is not among the routes
    // that platform API exposes -- and the screen opened on "게시 작업이
    // 실패했습니다 / not_found" before the reader had done anything.
    //
    // The same 404 also comes back for a project that does not exist, so the
    // two are indistinguishable from here; that is exactly why neither may be
    // called a failure. The one thing known is that there are no releases to
    // show, and the screen already has honest copy for that ("후보본 없음").
    //
    // Only the LIST is forgiving. A 404 on a SPECIFIC release means that
    // release is gone, which is a real and different answer, and 4xx/5xx of
    // every other kind still throw -- otherwise this fix would produce a
    // screen that can never report anything wrong.
    if (error instanceof PublicationReleaseApiError && error.status === 404) {
      return [];
    }
    throw error;
  }
  const root = asRecord(body);
  const values = Array.isArray(root.releases)
    ? root.releases
    : Array.isArray(root.items)
      ? root.items
      : [];
  return values.map(parsePublicationReleaseProjection);
}

export async function listPublicationTargetProfiles(
  signal?: AbortSignal,
): Promise<PublicationTargetProfileProjection[]> {
  const body = await publicationRequest("/api/publication-target-profiles", {
    signal,
  });
  const root = asRecord(body);
  if (!Array.isArray(root.profiles)) return [];
  return root.profiles.map(parsePublicationTargetProfileProjection).sort(
    (left, right) =>
      left.profileId.localeCompare(right.profileId) ||
      left.profileVersion.localeCompare(right.profileVersion, undefined, {
        numeric: true,
      }),
  );
}

export async function fetchPublicationRelease(
  projectId: string,
  releaseId: string,
  signal?: AbortSignal,
): Promise<PublicationReleaseProjection> {
  const body = await publicationRequest(releasePath(projectId, releaseId), {
    signal,
  });
  return parsePublicationReleaseProjection(body);
}

/**
 * The exact ontology fields a publication request may carry, or nothing.
 *
 * Re-parsed HERE, at the send site, rather than trusted from the caller's
 * type: the projection parser guards what came IN, and this guards what goes
 * OUT. A JS caller, a merged object, or a hand-assembled ref table cannot put
 * anything on the wire that is not the closed sixteen distinct roles.
 */
function ontologyRequestFields(
  refs: ApplicationOntologyPublicationRefs | undefined,
):
  | { applicationOntology: ApplicationOntologyPublicationRefs }
  | Record<string, never> {
  return refs === undefined
    ? {}
    : { applicationOntology: parsePublicationApplicationOntologyRefs(refs) };
}

export async function preparePublicationCandidate(
  projectId: string,
  input: PublicationCandidateRequest,
): Promise<PublicationReleaseProjection> {
  // Built field by field rather than serialized wholesale: whatever else a
  // caller attached to `input` does not reach the control plane.
  const requestBody = {
    targetProfileId: input.targetProfileId,
    ...(input.targetProfileVersion
      ? { targetProfileVersion: input.targetProfileVersion }
      : {}),
    ...(input.sourceRevisionHash
      ? { sourceRevisionHash: input.sourceRevisionHash }
      : {}),
    ...ontologyRequestFields(input.applicationOntology),
  };
  const body = await publicationRequest(
    `/api/projects/${encodeURIComponent(projectId)}/publications`,
    {
      method: "POST",
      body: JSON.stringify(requestBody),
    },
    PUBLICATION_PROOF_REQUEST_TIMEOUT_MS,
  );
  return parsePublicationReleaseProjection(body);
}

export async function runPublicationVerification(
  projectId: string,
  releaseId: string,
): Promise<PublicationReleaseProjection> {
  const body = await publicationRequest(
    `${releasePath(projectId, releaseId)}/verifications`,
    {
      method: "POST",
      body: JSON.stringify({ operation: "run-missing-verification" }),
    },
    PUBLICATION_PROOF_REQUEST_TIMEOUT_MS,
  );
  return parsePublicationReleaseProjection(body);
}

export async function requestPublicationStagingGrant(
  projectId: string,
  releaseId: string,
  adapterId: string,
  idempotencyKey: string,
  applicationOntology: ApplicationOntologyPublicationRefs | undefined,
): Promise<PublicationCredentialResult> {
  const body = await publicationRequest(
    `${releasePath(projectId, releaseId)}/grant`,
    {
      method: "POST",
      body: JSON.stringify({
        environment: "staging",
        operation: "deploy",
        adapterId,
        idempotencyKey,
        ...ontologyRequestFields(applicationOntology),
      }),
    },
  );
  return parseCredentialResult(body);
}

export async function requestPublicationAuthorization(
  projectId: string,
  releaseId: string,
  adapterId: string,
  idempotencyKey: string,
  applicationOntology: ApplicationOntologyPublicationRefs | undefined,
): Promise<PublicationCredentialResult> {
  const body = await publicationRequest(
    `${releasePath(projectId, releaseId)}/authorize`,
    {
      method: "POST",
      body: JSON.stringify({
        environment: "production",
        operation: "deploy",
        adapterId,
        idempotencyKey,
        ...ontologyRequestFields(applicationOntology),
      }),
    },
  );
  return parseCredentialResult(body);
}

export async function requestPublicationRollbackCredential(
  projectId: string,
  releaseId: string,
  environment: "preview" | "staging" | "production",
  targetDeploymentReceiptRef: PublicationContentRef,
  confirmation: PublicationRollbackTransportRequest["confirmation"],
  idempotencyKey: string,
  applicationOntology: ApplicationOntologyPublicationRefs | undefined,
): Promise<PublicationCredentialResult> {
  const action = environment === "production" ? "authorize" : "grant";
  const body = await publicationRequest(
    `${releasePath(projectId, releaseId)}/${action}`,
    {
      method: "POST",
      body: JSON.stringify({
        environment,
        operation: "rollback",
        targetDeploymentReceiptRef,
        confirmation,
        idempotencyKey,
        ...ontologyRequestFields(applicationOntology),
      }),
    },
  );
  return parseCredentialResult(body);
}

export async function requestPublicationTakeDownCredential(
  projectId: string,
  releaseId: string,
  environment: "preview" | "staging" | "production",
  targetDeploymentReceiptRef: PublicationContentRef,
  idempotencyKey: string,
  applicationOntology: ApplicationOntologyPublicationRefs | undefined,
): Promise<PublicationCredentialResult> {
  const action = environment === "production" ? "authorize" : "grant";
  const body = await publicationRequest(
    `${releasePath(projectId, releaseId)}/${action}`,
    {
      method: "POST",
      body: JSON.stringify({
        environment,
        operation: "take-down",
        targetDeploymentReceiptRef,
        idempotencyKey,
        ...ontologyRequestFields(applicationOntology),
      }),
    },
  );
  return parseCredentialResult(body);
}

export async function deployPublicationRelease(
  projectId: string,
  releaseId: string,
  input: PublicationDeployTransportRequest,
): Promise<PublicationReleaseProjection> {
  const { applicationOntology, ...transport } = input;
  const body = await publicationRequest(
    `${releasePath(projectId, releaseId)}/deploy`,
    {
      method: "POST",
      body: JSON.stringify({
        ...transport,
        ...ontologyRequestFields(applicationOntology),
      }),
    },
  );
  return parsePublicationReleaseProjection(body);
}

function parseCredentialResult(body: unknown): PublicationCredentialResult {
  const root = asRecord(body);
  const executionCredential = firstString(root.executionCredential);
  if (!executionCredential)
    throw new Error("Publication execution credential was not returned.");
  return {
    release: parsePublicationReleaseProjection(
      root.projection ?? root.release ?? root,
    ),
    executionCredential,
  };
}

export async function refreshPublicationReadback(
  projectId: string,
  releaseId: string,
): Promise<PublicationReleaseProjection> {
  const body = await publicationRequest(
    `${releasePath(projectId, releaseId)}/readback`,
    {
      method: "POST",
      body: JSON.stringify({ operation: "refresh" }),
    },
  );
  return parsePublicationReleaseProjection(body);
}

export async function rollbackPublicationRelease(
  projectId: string,
  releaseId: string,
  input: PublicationRollbackTransportRequest,
): Promise<PublicationReleaseProjection> {
  const { applicationOntology, ...transport } = input;
  const body = await publicationRequest(
    `${releasePath(projectId, releaseId)}/rollback`,
    {
      method: "POST",
      body: JSON.stringify({
        ...transport,
        ...ontologyRequestFields(applicationOntology),
      }),
    },
  );
  return parsePublicationReleaseProjection(body);
}

export async function takeDownPublicationRelease(
  projectId: string,
  releaseId: string,
  input: PublicationTakeDownTransportRequest,
): Promise<PublicationReleaseProjection> {
  const { applicationOntology, ...transport } = input;
  const body = await publicationRequest(
    `${releasePath(projectId, releaseId)}/take-down`,
    {
      method: "POST",
      body: JSON.stringify({
        ...transport,
        ...ontologyRequestFields(applicationOntology),
      }),
    },
  );
  return parsePublicationReleaseProjection(body);
}

function releasePath(projectId: string, releaseId: string): string {
  return `/api/projects/${encodeURIComponent(projectId)}/publications/${encodeURIComponent(releaseId)}`;
}

async function publicationRequest(
  path: string,
  init: RequestInit = {},
  timeoutMs = PUBLICATION_REQUEST_TIMEOUT_MS,
): Promise<unknown> {
  const accessToken = await getStudioAccessToken();
  if (!accessToken) throw new Error("Not authenticated");

  const controller = new AbortController();
  const externalSignal = init.signal;
  const abortFromCaller = () => controller.abort();
  if (externalSignal?.aborted) controller.abort();
  else
    externalSignal?.addEventListener("abort", abortFromCaller, {
      once: true,
    });
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const headers = withCogoDevProxyHeaders(init.headers);
    headers.set("Authorization", `Bearer ${accessToken}`);
    if (init.body && !headers.has("Content-Type"))
      headers.set("Content-Type", "application/json");
    const response = await fetch(cogoPlatformApiUrl(path), {
      ...init,
      headers,
      signal: controller.signal,
    });
    const body = await response.json().catch(() => null);
    if (!response.ok)
      throw new PublicationReleaseApiError(response.status, body);
    return body;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error(
        "Publication request timed out before status confirmation. Refresh the release state before retrying.",
      );
    }
    throw error;
  } finally {
    clearTimeout(timer);
    externalSignal?.removeEventListener("abort", abortFromCaller);
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function firstString(...values: unknown[]): string | undefined {
  return values
    .find(
      (value): value is string =>
        typeof value === "string" && value.trim().length > 0,
    )
    ?.trim();
}
